import {
  splitToneNumbersPinyinSyllables,
  toDiacriticPinyin,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { Text, Text as RNText } from "@-/dev.improve.haniverse.components.ui.text";
import {
  PinyinSoundButton,
  usePinyinSoundButtonGate,
} from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-sound-button";
import {
  useActorToneColors,
  useEffectiveIsToneColorsEnabled,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.actor.user";
import { toneForPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.transcription";
import { useMemo } from "react";
import { View } from "@-/com.npmjs.react-native";
import { hasLength } from "@-/com.npmjs.ts-array-length";

type PinyinAudioCard = {
  pinyinList: readonly { toneNumbersPinyin: string }[];
} & ({ grapheme: string } | { front: string });

/**
 * Comma-separated pinyin readings led by an audio button that plays the card's
 * pinyin in sequence when pressed. Readings wrap as whole units. Each reading
 * respects the user's tone-color settings. The sound button is gated off when
 * the card has no playable audio.
 *
 * Shared between the info dictionary morpheme section and the mnemonics
 * meaning-view hero — both render the same pressable pinyin label.
 */
export function PinyinPronunciation({
  alignment = "center",
  card,
  pinyinList,
}: {
  alignment?: "center" | "start";
  card: PinyinAudioCard;
  /** Display strings (tone-marked), already derived from the card's readings. */
  pinyinList: readonly string[];
}) {
  const isToneColorsEnabled = useEffectiveIsToneColorsEnabled();
  const toneColors = useActorToneColors();
  const entries = useMemo(
    () =>
      pinyinList.map((pinyin, index) => ({
        pinyin,
        syllables: splitToneNumbersPinyinSyllables(
          card.pinyinList[index]?.toneNumbersPinyin ?? pinyin,
        ).map((syllable) => {
          const tone = toneForPinyin(syllable);
          return {
            color: isToneColorsEnabled && tone !== null ? toneColors[tone] : null,
            pinyin: toDiacriticPinyin(syllable),
          };
        }),
      })),
    [card.pinyinList, isToneColorsEnabled, pinyinList, toneColors],
  );
  const showPlaceholder = hasLength(entries, 0);
  const soundButtonGate = usePinyinSoundButtonGate(card);

  return (
    <View
      // `items-start` (not `items-center`) so the speaker stays beside the FIRST line once the
      // readings wrap — centering would float it halfway down a tall multi-line block.
      className={
        alignment === "start"
          ? "w-full shrink-0 flex-row items-start justify-start gap-1.5"
          : "w-full shrink-0 flex-row items-start justify-center gap-1.5"
      }
    >
      {/* The speaker leads the readings. h-5 matches the text-sm line height (20px), so the 16px
          icon optically centers on the first line instead of hugging the top of the row. */}
      {soundButtonGate !== null && (
        <PinyinSoundButton
          className="h-5 shrink-0 items-center justify-center"
          gate={soundButtonGate}
          mode="sequence"
          size={16}
        />
      )}
      {showPlaceholder ? (
        <Text className="text-sm leading-5 text-muted-foreground">-</Text>
      ) : (
        // Each reading is its own unbreakable item, so a narrow column wraps BETWEEN readings
        // (never inside one or with a dangling comma) and every line stays aligned. Readings are
        // never truncated: each is a distinct pronunciation the learner has to be able to read.
        <View
          className={
            alignment === "start"
              ? "min-w-0 shrink flex-row flex-wrap justify-start gap-x-1"
              : "min-w-0 shrink flex-row flex-wrap justify-center gap-x-1"
          }
        >
          {entries.map((entry, index) => (
            <Text
              className="text-sm leading-5 text-muted-foreground"
              key={`${entry.pinyin}-${index}`}
            >
              {entry.syllables.map((syllable, syllableIndex) => (
                <RNText
                  key={syllableIndex}
                  style={syllable.color !== null ? { color: syllable.color } : undefined}
                >
                  {syllableIndex > 0 ? " " : null}
                  {syllable.pinyin}
                </RNText>
              ))}
              {index < entries.length - 1 ? "," : null}
            </Text>
          ))}
        </View>
      )}
    </View>
  );
}
